<script setup lang="ts">
import {useExperienceStore} from "@/stores/experience.store";
import {useRouter} from "vue-router";
import {storeToRefs} from "pinia";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import ApodisCoinService from "../../core/services/ApodisCoinService";
import StripeService from "../../core/services/StripeService";
import { Progress } from "@/core/components/ui/progress";
import ExperienceService from "@/core/services/ExperienceService";
import {useSystemStore} from "@/stores/system.store";
import {useToastStore} from "@/stores/toast.store";
import {useExtensionStore} from "@/stores/extension.store";
import {apodisCoinApi} from "@/container";
import {ApodisCoinPurchasable} from "@/core/interfaces/ApodisCoin";
import {useCreditStore} from "@/stores/credit.store";
import {onMounted, onUnmounted} from "vue";

const {experience} = storeToRefs(useExperienceStore());
const {coinStoreItems, coinBalance} = storeToRefs(useSystemStore());
const {loadApodisCoins, reloadApodisCoinsAfterCheckout} = useSystemStore();
const {loadExtensions} = useExtensionStore();
const {refresh} = useCreditStore();
const router = useRouter();
const {showSuccess} = useToastStore();

/** When component is mounted */
onMounted(async () => {
    window.ipcRenderer.on("stripe-checkout-success", () => {
        refresh();
        reloadApodisCoinsAfterCheckout();
    });
});

/** When component is unmounted */
onUnmounted(() => {
    window.ipcRenderer.removeAllListeners("stripe-checkout-success");
});

/**
 * Determine if the current organization has enough coins to buy the extension.
 *
 * If the user is already subscribed to the extension, and there is no next invoice price, it means that the user
 * has already set a discount for the extension, so he can't buy it again.
 *
 * If the user is already subscribed to the extension, and there is a next invoice price, it means that the user
 * has already subscribed to the extension, and no discount is applied. So he can purchase next cycle for free.
 *
 * If the user is not subscribed to the extension, we check if the user has enough coins to buy the extension.
 *
 * @type {boolean}
 */
const canPurchase = (item: ApodisCoinPurchasable) => {
    const balanceInEuro = ApodisCoinService().convertApodisCoinToEuro(coinBalance.value);
    if (item.subscribed && !item.next_invoice_price) {
        return false;
    }


    if (item.subscribed && item.next_invoice_price) {
        return balanceInEuro >= item.next_invoice_price;
    }


    if (!priceToPay(item)) {
        return false;
    }

    if (isNaN(priceToPay(item)) || !coinBalance.value) {
        return false;
    }

    return balanceInEuro >= priceToPay(item);
};

/**
 * Get the price in euro to pay for the extension subscription.
 *
 * @type {number}
 */
const priceToPay = (item: ApodisCoinPurchasable) => {
    if (item.next_invoice_price) {
        return item.next_invoice_price;
    }

    if (!item.prices?.length) {
        return 0;
    }

    const priceIndex = item.prices.findIndex(price => price.is_default);
    if (priceIndex !== -1 && item.prices[priceIndex]?.price) {
        return parseFloat(item.prices[priceIndex].price);
    }

    return item.prices[0]?.price ? parseFloat(item.prices[0].price) : 0;
};

/**
 * Get purchasable type.
 *
 * @param item
 */
const purchasableType = (item: ApodisCoinPurchasable): string => {
    if ('extendable_type' in item) {
        return 'extension';
    } else if ('sales_spitch' in item) {
        return 'subscription_plan';
    }

    return 'stripe-item';
};

/**
 * Pay with coins
 */
const pay = (item: ApodisCoinPurchasable) => {
    return apodisCoinApi().purchase(item.id, purchasableType(item))
        .then((response) => {
            if (response.type === "checkout") {
                window.ipcRenderer.stripeCheckout(response.data.session.url);
            } else {
                showSuccess({content: "Extension achetée avec succès !"});
                loadExtensions();
            }
        })
        .finally(() => loadApodisCoins());
};
</script>

<template>
  <div class="gradient pb-6 px-4">
    <div
      v-if="experience"
      class="flex gradient relative justify-end items-end h-28 cursor-pointer hover:opacity-95"
      @click="() => router.push({name: 'experience'})"
    >
      <div class="absolute bg-white rounded-lg px-4 py-2 top-4 right-0 mr-24">
        <p class="text-black font-semibold">
          Remplissez des missions, gagnez des Apodis Coins, et échangez-les contre des réductions !
        </p>
      </div>
      <div class="flex flex-col">
        <img :src="experience.tiers.current.full_image_path" class="h-20" alt="Pharmacie">
        <Progress :model-value="ExperienceService().progress(experience).progress" indicator-class="bg-red" />
      </div>
    </div>
    <BaseCard class="p-4 mt-4">
      <h3>Dépensez vos Apodis Coins pour obtenir des réductions</h3>
      <div v-if="coinStoreItems?.length" class="grid grid-cols-2 gap-6 py-4">
        <BaseCard
          v-for="item in coinStoreItems"
          :key="item.id"
          class="p-4 border-primary/50 border-b-2 border-r-2"
        >
          <h3>{{ item.name }}</h3>
          <p v-if="item.prices" class="line-through">
            {{ StripeService().startingReadablePrice(item.prices) }}
          </p>
          <BaseButton primary :disabled="!canPurchase(item)" class="mt-2" @click="pay(item)">
            <p>Utiliser {{ ApodisCoinService().convertEuroToApodisCoin(priceToPay(item)) }}</p>
            <img
              src="@/assets/images/apodis-coin.png"
              alt="apodis-coin"
              class="w-7 h-7 ml-2"
            >
          </BaseButton>
        </BaseCard>
      </div>
      <div v-else>
        <p>Aucun produit disponible à l'achat grâce aux apodis coins</p>
      </div>
    </BaseCard>
  </div>
</template>
